import { FC, useEffect } from 'react';
import { Form } from 'rsuite';
import { useFormik, yupToFormErrors } from 'formik';
import { useCampaignStore } from 'store/campaignStore';

import { Section } from 'components/Rsuite/Section';
import { RsButton } from 'components/Rsuite/Button';
import useBaseForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useBaseForm';
import useRecommendForm from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useRecommendForm';
import useRecommendValidation from 'containers/RtbCampaigns/RtbCampaignSinglePage/Hooks/useRecommendValidation';
import { RsInput } from 'components/Rsuite/Input';
import styles from './baseForm.module.scss';
import FutureDateRangePicker from 'components/DateRangePicker/FutureDateRangePicker';
import moment from 'moment';
import _ from 'lodash';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager,
} from 'core/rtbCampaign/RtbCampaignManager';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useProductStore } from 'store/productStore';
import BidPrice from './Common/BidPrice';
import BudgetRadios from './Recommend/BudgetRadios';
import { BrandSelection } from './Common/BrandSelection';
import { ActionType, SelectPattern } from 'core/rtbCampaign/singlePage/common';
import { RsDateRangePicker } from 'components/Rsuite/DateRangePicker';

interface ResommendFormProps {
  defaultForm: any;
}

const campaignManager: RtbCampaignManager = new DefaultRtbCampaignManager();

const RecommendForm: FC<ResommendFormProps> = ({ defaultForm }) => {
  const history = useHistory();

  const { actionType, loading, handleLoading } = useCampaignStore();
  const { selectedProducts, selectedAutoProducts } = useProductStore();

  // init Formik
  const { values, setFieldValue, setErrors, handleSubmit } = useFormik({
    initialValues: { ...defaultForm },
    onSubmit: async (values) => {
      const campaign = {
        ...values,
        products:
          values.basic.selectPattern === SelectPattern.AUTO
            ? selectedAutoProducts
            : selectedProducts,
      };
      // return
      validationSchema
        .validate(values, { abortEarly: false })
        .then(async function () {
          handleLoading(true);
          try {
            switch (actionType) {
              case 'edit':
                await campaignManager.updateCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                history.push(`/orders/${order?.orderNumber}`);
                break;
              case 'create':
              case 'copy':
                await campaignManager.createCampaign(
                  campaign,
                  order.l1Objects[0]?.l1ObjectId,
                );
                toast.success('成功');
                history.push(`/orders/${order?.orderNumber}`);
                break;
              default:
                break;
            }
          } catch (error) {
            console.error('Failed to create campaign:', error);
            handleLoading(false);
          }
        })
        .catch(function (err) {
          setErrors(yupToFormErrors(err));
          console.log('errors', yupToFormErrors(err));
        });
    },
  });

  // 提交表單
  const onSubmit = () => {
    handleSubmit();
  };

  const { campaignMinDate, handleCampaignNameChange, handleBack } = useBaseForm(
    {
      values,
      setFieldValue,
    },
  );

  const {
    order,
    diffDays,
    brandOptions,
    selectBrand,
    handleBrandChange,
    handleDateRangeChange,
    handleChangeStartDate,
    handleChangeEndDate,
    handleBudgetTypeChange,
    handleBudgetChange,
    handleChangeBidPrice,
  } = useRecommendForm({
    defaultForm,
    values,
    setFieldValue,
  });

  const { errors, validationSchema, handleValidation } = useRecommendValidation(
    {
      defaultForm,
      newForm: values,
    },
  );

  useEffect(() => {
    handleValidation();
  }, [values]);

  return (
    <>
      {loading && <LoadingIndicator />}
      <Section title='廣告基本設定'>
        <Form layout='horizontal' className='w-[1260px]'>
          <Form.Group>
            <Form.ControlLabel>活動名稱</Form.ControlLabel>
            <RsInput
              value={values.basic?.name}
              onChange={handleCampaignNameChange}
              className='!w-80'
              errorMessage={errors?.basic?.name}
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>商品設定</Form.ControlLabel>
            <div className='relative float-left py-2'>
              <div className='mb-2'>
                <span className='mb-1 inline-block text-sm text-[#414651]'>
                  智能選品
                </span>
                <Form.HelpText>動態調整投廣商品</Form.HelpText>
              </div>
              <BrandSelection
                data={brandOptions}
                value={selectBrand}
                onChange={handleBrandChange}
                helpText={<>建立活動後，即不可更換品牌</>}
                errorMessage={{ autoProducts: errors?.autoProducts }}
              />
            </div>
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告期間</Form.ControlLabel>
            <div className='relative float-left'>
              <RsDateRangePicker
                startDate={moment(values.basic.startDate).format(
                  'YYYY-MM-DD HH:mm',
                )}
                endDate={moment(values.basic.endDate).format(
                  'YYYY-MM-DD HH:mm',
                )}
                minDate={moment(campaignMinDate).format('YYYY-MM-DD HH:mm')}
                maxDate={moment(order.endDate)
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm')}
                showTimePicker={true}
                onStartDateChange={handleChangeStartDate}
                onEndDateChange={handleChangeEndDate}
                startDatePickerDisabled={
                  actionType === ActionType.EDIT &&
                  new Date(defaultForm.basic.startDate) <= new Date()
                }
              />
              <div className='flex items-center gap-1'>
                <Form.HelpText>
                  建議活動至少大於兩周，以達到系統最佳學習效果
                  <br />
                  廣告活動開始後，將無法修改開始日期
                  <br />共 {diffDays} 天
                </Form.HelpText>
              </div>
              <Form.ErrorMessage
                show={
                  !![errors?.basic?.startDate, errors?.basic?.endDate]
                    .filter(Boolean)
                    .join(' / ')
                }
                placement='topEnd'
              >
                {[errors?.basic?.startDate, errors?.basic?.endDate]
                  .filter(Boolean)
                  .join(' / ')}
              </Form.ErrorMessage>
            </div>
          </Form.Group>
          <Form.Group controlId='data-range-radio'>
            <Form.ControlLabel>廣告預算</Form.ControlLabel>
            <BudgetRadios
              diffDays={diffDays}
              values={values}
              errors={errors}
              onBudgetTypeChange={handleBudgetTypeChange}
              onBudgetChange={handleBudgetChange}
            />
          </Form.Group>
        </Form>
      </Section>
      <Section title='出價設定'>
        <Form layout='horizontal'>
          <Form.Group className='w-[37rem]'>
            <Form.ControlLabel>出價上限</Form.ControlLabel>
            <BidPrice
              currency={order.currency}
              values={values}
              suggestCpc={[3, 10]}
              errors={errors}
              onBidPriceChange={handleChangeBidPrice}
            />
          </Form.Group>
        </Form>
      </Section>
      <div className='my-8 ml-8 flex items-center gap-4'>
        <RsButton
          appearance='primary'
          onClick={onSubmit}
          disabled={!_.isEmpty(errors)}
        >
          完成
        </RsButton>
        <RsButton appearance='ghost' onClick={handleBack}>
          取消
        </RsButton>
      </div>
    </>
  );
};

export default RecommendForm;
